import {
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Input, useCombobox } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { FormLabel } from '../formlabel/FormLabel';

import { PeriodPickerDropdown } from './PeriodPickerDropdown';
import {
  comparePeriods,
  getCurrentPeriod,
  getPeriodEndIso,
  getPeriodStartIso,
  isoToPeriod,
  PeriodRange,
  PeriodRangePreset,
  PeriodType,
  PeriodValue,
} from './periodRange';
import { PeriodRangeInput } from './PeriodRangeInput';

import datePickerStyles from '../datepicker/date-picker.module.scss';
import inputStyles from '../input/input.module.scss';

export type SharedPeriodRangePickerProps = {
  label?: ReactNode | ReactNode[];
  description?: ReactNode | ReactNode[];
  required?: boolean;
  error?: string | boolean;
  defaultValue?: PeriodRange;
  value?: PeriodRange;
  onChange?: (value: PeriodRange) => void;
  onCommit?: (value: PeriodRange) => void;
  presets?: PeriodRangePreset[];
  minDate?: string;
  maxDate?: string;
  placeholderFrom?: string;
  placeholderTo?: string;
  pageSize?: number;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  withinPortal?: boolean;
  icon?: string;
  className?: string;
  'data-testid'?: string;
};

type PeriodRangePickerProps = SharedPeriodRangePickerProps & {
  type: PeriodType;
  periodsPerYear: number;
  gridColumns?: number;
  format: (value: PeriodValue) => string;
  parse: (input: string) => PeriodValue | undefined;
  formatGridPeriod: (period: number) => string;
};

type Endpoint = keyof PeriodRange;

const OPPOSITE_ENDPOINT: Record<Endpoint, Endpoint> = {
  start: 'end',
  end: 'start',
};

function rangesEqual(a: PeriodRange, b: PeriodRange) {
  return a.start === b.start && a.end === b.end;
}

function getPresetValue(preset: PeriodRangePreset) {
  return typeof preset.value === 'function' ? preset.value() : preset.value;
}

function normalizePeriodRange(range: PeriodRange, type: PeriodType) {
  const start = isoToPeriod(range.start, type);
  const end = isoToPeriod(range.end, type);
  if (
    (range.start !== undefined && !start) ||
    (range.end !== undefined && !end)
  ) {
    return undefined;
  }
  return {
    start: start ? getPeriodStartIso(start, type) : undefined,
    end: end ? getPeriodEndIso(end, type) : undefined,
  };
}

export function PeriodRangePicker({
  type,
  periodsPerYear,
  gridColumns = periodsPerYear,
  format,
  parse,
  formatGridPeriod,
  label,
  description,
  required,
  error,
  value,
  defaultValue = {},
  onChange,
  onCommit,
  presets = [],
  minDate,
  maxDate,
  placeholderFrom,
  placeholderTo,
  pageSize = 4,
  disabled = false,
  loading = false,
  readOnly = false,
  clearable = true,
  withinPortal = true,
  icon = 'event',
  className,
  'data-testid': testId = `${type}-range-picker`,
}: PeriodRangePickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const [focusedEndpoint, setFocusedEndpoint] = useState<Endpoint>();
  const [inputResetVersions, setInputResetVersions] = useState({
    start: 0,
    end: 0,
  });
  const currentPeriod = useMemo(() => getCurrentPeriod(type), [type]);
  const yearsBeforeAnchor = Math.floor((pageSize - 1) / 2);
  const [pageStart, setPageStart] = useState(
    currentPeriod.year - yearsBeforeAnchor
  );
  const [range, setRange] = useUncontrolled<PeriodRange>({
    value,
    defaultValue,
    onChange,
  });
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const lastCommittedRange = useRef(range);
  const minimum = isoToPeriod(minDate, type);
  const maximum = isoToPeriod(maxDate, type);

  const commitRange = useCallback(
    (nextRange = rangeRef.current) => {
      if (!rangesEqual(lastCommittedRange.current, nextRange)) {
        lastCommittedRange.current = nextRange;
        onCommit?.(nextRange);
      }
    },
    [onCommit]
  );

  const combobox = useCombobox({
    onDropdownClose: () => {
      setFocusedEndpoint(undefined);
      commitRange();
    },
  });

  const updateRange = useCallback(
    (nextRange: PeriodRange) => {
      rangeRef.current = nextRange;
      setRange(nextRange);
    },
    [setRange]
  );

  const normalizeEndpoint = useCallback(
    (endpoint: Endpoint, period: PeriodValue) =>
      endpoint === 'start'
        ? getPeriodStartIso(period, type)
        : getPeriodEndIso(period, type),
    [type]
  );

  const isWithinBounds = useCallback(
    (period: PeriodValue) =>
      (!minimum || comparePeriods(period, minimum) >= 0) &&
      (!maximum || comparePeriods(period, maximum) <= 0),
    [maximum, minimum]
  );

  const isValidEndpoint = useCallback(
    (endpoint: Endpoint, period: PeriodValue) => {
      if (!isWithinBounds(period)) {
        return false;
      }

      const oppositePeriod = isoToPeriod(
        rangeRef.current[OPPOSITE_ENDPOINT[endpoint]],
        type
      );
      if (!oppositePeriod) {
        return true;
      }

      const comparison = comparePeriods(period, oppositePeriod);
      return endpoint === 'start' ? comparison <= 0 : comparison >= 0;
    },
    [isWithinBounds, type]
  );

  const applyPeriod = useCallback(
    (endpoint: Endpoint, period: PeriodValue) => {
      if (!isValidEndpoint(endpoint, period)) {
        return;
      }
      updateRange({
        ...rangeRef.current,
        [endpoint]: normalizeEndpoint(endpoint, period),
      });
    },
    [isValidEndpoint, normalizeEndpoint, updateRange]
  );

  function focusEndpoint(endpoint: Endpoint) {
    const startsInteraction = !combobox.dropdownOpened;
    if (startsInteraction) {
      lastCommittedRange.current = rangeRef.current;
    }
    setFocusedEndpoint(endpoint);
    const selected = isoToPeriod(rangeRef.current[endpoint], type);
    if (selected || startsInteraction) {
      setPageStart((selected ?? currentPeriod).year - yearsBeforeAnchor);
    }
    combobox.openDropdown();
  }

  function clearEndpoint(endpoint: Endpoint) {
    const nextRange = { ...rangeRef.current, [endpoint]: undefined };
    updateRange(nextRange);
    lastCommittedRange.current = nextRange;
    onCommit?.(nextRange);
  }

  function selectPeriod(period: PeriodValue) {
    if (!focusedEndpoint || !isValidEndpoint(focusedEndpoint, period)) {
      return;
    }

    setInputResetVersions((versions) => ({
      ...versions,
      [focusedEndpoint]: versions[focusedEndpoint] + 1,
    }));
    applyPeriod(focusedEndpoint, period);
    if (focusedEndpoint === 'start' && rangeRef.current.end == null) {
      setFocusedEndpoint('end');
      endRef.current?.focus();
    } else {
      combobox.closeDropdown();
    }
  }

  function applyPreset(preset: PeriodRangePreset) {
    const normalizedRange = normalizePeriodRange(getPresetValue(preset), type);
    if (!normalizedRange) {
      return;
    }
    const start = isoToPeriod(normalizedRange.start, type);
    const end = isoToPeriod(normalizedRange.end, type);
    if (
      (start && !isWithinBounds(start)) ||
      (end && !isWithinBounds(end)) ||
      (start && end && comparePeriods(start, end) > 0)
    ) {
      return;
    }

    setInputResetVersions((versions) => ({
      start: versions.start + 1,
      end: versions.end + 1,
    }));
    updateRange(normalizedRange);
    lastCommittedRange.current = normalizedRange;
    onCommit?.(normalizedRange);
    startRef.current?.blur();
    endRef.current?.blur();
    combobox.closeDropdown();
  }

  const years = Array.from(
    { length: pageSize },
    (_, index) => pageStart + index
  );
  const selectedStart = isoToPeriod(range.start, type);
  const selectedEnd = isoToPeriod(range.end, type);
  const selectedEndpoint = focusedEndpoint
    ? isoToPeriod(range[focusedEndpoint], type)
    : undefined;
  const oppositeEndpoint = focusedEndpoint
    ? isoToPeriod(range[OPPOSITE_ENDPOINT[focusedEndpoint]], type)
    : undefined;
  const rangeIsInvalid =
    (selectedStart && !isWithinBounds(selectedStart)) ||
    (selectedEnd && !isWithinBounds(selectedEnd)) ||
    (selectedStart &&
      selectedEnd &&
      comparePeriods(selectedStart, selectedEnd) > 0);
  const isUnavailable = disabled || loading || readOnly;

  return (
    <div
      className={clsx(datePickerStyles['root'], className)}
      data-testid={testId}
    >
      <FormLabel
        id={id}
        label={label}
        description={description}
        required={required}
        disabled={disabled}
        loading={loading}
        error={error}
      />
      <Combobox
        unstyled
        disabled={isUnavailable}
        store={combobox}
        withinPortal={withinPortal}
        classNames={{ dropdown: datePickerStyles['dropdown'] }}
      >
        <Combobox.EventsTarget>
          <div
            className={datePickerStyles['dates-wrapper']}
            onBlur={() =>
              setTimeout(() => {
                if (
                  document.activeElement !== startRef.current &&
                  document.activeElement !== endRef.current
                ) {
                  combobox.closeDropdown();
                }
              })
            }
          >
            <Combobox.DropdownTarget>
              <div className={datePickerStyles['dates-wrapper']}>
                <PeriodRangeInput
                  id={id}
                  data-testid={`${type}-range-start`}
                  ref={startRef}
                  value={selectedStart}
                  format={format}
                  parse={parse}
                  validate={(period) => isValidEndpoint('start', period)}
                  focused={focusedEndpoint === 'start'}
                  disabled={disabled}
                  loading={loading}
                  readOnly={readOnly}
                  clearable={clearable}
                  icon={icon}
                  placeholder={
                    placeholderFrom ?? t('app.periodRangePicker.from')
                  }
                  externalError={Boolean(error) || Boolean(rangeIsInvalid)}
                  resetVersion={inputResetVersions.start}
                  onClear={() => clearEndpoint('start')}
                  onFocus={() => {
                    if (!isUnavailable) {
                      focusEndpoint('start');
                    }
                  }}
                  onValidChange={(period) => applyPeriod('start', period)}
                  onValidCommit={() => {
                    if (rangeRef.current.end == null) {
                      endRef.current?.focus();
                    } else {
                      combobox.closeDropdown();
                    }
                  }}
                />
                <span
                  className={datePickerStyles['input-separator']}
                  data-disabled={isUnavailable || undefined}
                >
                  -
                </span>
                <PeriodRangeInput
                  data-testid={`${type}-range-end`}
                  ref={endRef}
                  value={selectedEnd}
                  format={format}
                  parse={parse}
                  validate={(period) => isValidEndpoint('end', period)}
                  focused={focusedEndpoint === 'end'}
                  disabled={disabled}
                  loading={loading}
                  readOnly={readOnly}
                  clearable={clearable}
                  icon={icon}
                  placeholder={placeholderTo ?? t('app.periodRangePicker.to')}
                  externalError={Boolean(error) || Boolean(rangeIsInvalid)}
                  resetVersion={inputResetVersions.end}
                  onClear={() => clearEndpoint('end')}
                  onFocus={() => {
                    if (!isUnavailable) {
                      focusEndpoint('end');
                    }
                  }}
                  onValidChange={(period) => applyPeriod('end', period)}
                  onValidCommit={() => combobox.closeDropdown()}
                />
              </div>
            </Combobox.DropdownTarget>
          </div>
        </Combobox.EventsTarget>

        <PeriodPickerDropdown
          years={years}
          periodsPerYear={periodsPerYear}
          gridColumns={gridColumns}
          currentPeriod={currentPeriod}
          formatGridPeriod={formatGridPeriod}
          arrowPosition={focusedEndpoint}
          isUnavailable={(period) =>
            !isValidEndpoint(focusedEndpoint ?? 'start', period)
          }
          isSelected={(period) =>
            selectedEndpoint != null &&
            comparePeriods(period, selectedEndpoint) === 0
          }
          isOppositeSelected={(period) =>
            oppositeEndpoint != null &&
            comparePeriods(period, oppositeEndpoint) === 0
          }
          isInRange={(period) =>
            Boolean(
              selectedStart &&
              selectedEnd &&
              comparePeriods(period, selectedStart) > 0 &&
              comparePeriods(period, selectedEnd) < 0
            )
          }
          onPageEarlier={() => setPageStart((year) => year - pageSize)}
          onPageLater={() => setPageStart((year) => year + pageSize)}
          onSelectPeriod={selectPeriod}
          footer={
            presets.length > 0 ? (
              <div className={datePickerStyles['period-selection']}>
                {presets.map((preset) => {
                  const presetValue = normalizePeriodRange(
                    getPresetValue(preset),
                    type
                  );
                  const isSelected =
                    presetValue != null && rangesEqual(range, presetValue);
                  return (
                    <Button
                      key={preset.label}
                      aria-pressed={isSelected}
                      variant={isSelected ? 'primary' : 'secondary'}
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.label}
                    </Button>
                  );
                })}
              </div>
            ) : undefined
          }
        />
      </Combobox>
      {typeof error === 'string' && error && (
        <Input.Error className={inputStyles['input-error']}>
          {error}
        </Input.Error>
      )}
    </div>
  );
}
